import { useState } from "react";
import { clock } from "../api";
import { AppEpisode, country, date, language, route } from "./api";
import { useApp } from "./context";
import { usePlayer, PlayButton } from "./player";
import { Artwork, Empty, Icon, IconButton } from "./visual";
export function EpisodeCard({
  episode,
  index = 0,
}: {
  episode: AppEpisode;
  index?: number;
}) {
  const { title, t, lang, localization } = useApp(),
    p = usePlayer();
  return (
    <article
      className="episode-card"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <div className="card-art">
        <a href={route("episode", episode.id)} tabIndex={-1} aria-hidden="true">
          <Artwork episode={episode} />
        </a>
        <span className="art-duration">
          {clock(episode.recording?.duration_ms)}
        </span>
        <div className="art-play">
          <PlayButton episode={episode} />
        </div>
      </div>
      <div className="card-body">
        <div className="card-meta">
          <a href={route("station", episode.station_id)}>
            {episode.station_name}
          </a>
          <span>{date(episode.broadcast_date, lang)}</span>
        </div>
        <h3>
          <a
            title={
              localization.episodes[episode.id]?.automatic
                ? t("translated") + ": " + episode.title
                : undefined
            }
            href={route("episode", episode.id)}
            dir="auto"
          >
            {title(episode)}
          </a>
        </h3>
        <div className="card-bottom">
          <a href={route("show", episode.show_id)} dir="auto">
            {episode.show_title}
          </a>
          <IconButton
            name="plus"
            label={t("queue") + " · " + title(episode)}
            onClick={() => p.enqueue(episode)}
          />
        </div>
      </div>
    </article>
  );
}
export function EpisodeGrid({ episodes }: { episodes: AppEpisode[] }) {
  const { t } = useApp();
  return episodes.length ? (
    <div className="episode-grid">
      {episodes.map((ep, i) => (
        <EpisodeCard key={ep.id} episode={ep} index={i} />
      ))}
    </div>
  ) : (
    <Empty>{t("empty")}</Empty>
  );
}
export function SectionHead({
  label,
  count,
  href,
}: {
  label: string;
  count?: number;
  href?: string;
}) {
  const { t } = useApp();
  return (
    <div className="section-head">
      <h2>
        {label}
        {count !== undefined && <sup>{String(count).padStart(2, "0")}</sup>}
      </h2>
      {href && (
        <a className="text-link" href={href}>
          {t("viewAll")}
          <Icon name="arrow" size={16} />
        </a>
      )}
    </div>
  );
}
export function PageHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <header className="page-heading">
      <span className="eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </header>
  );
}
export default function Discover() {
  const { catalogue, t, lang } = useApp();
  const [filter, setFilter] = useState("all"),
    [sort, setSort] = useState("added");
  const languages = [
    ...new Set(catalogue.episodes.flatMap((ep) => ep.languages)),
  ].sort();
  const episodes = catalogue.episodes
    .filter((ep) => filter === "all" || ep.languages.includes(filter))
    .sort((a, b) =>
      (sort === "broadcast"
        ? b.broadcast_date || ""
        : b.imported_at
      ).localeCompare(
        sort === "broadcast" ? a.broadcast_date || "" : a.imported_at,
      ),
    );
  return (
    <>
      <section id="collection" className="collection-section">
        <SectionHead label={t("latest")} count={catalogue.episodes.length} />
        <div className="filter-bar">
          <div className="filter-chips">
            <button
              aria-pressed={filter === "all"}
              onClick={() => setFilter("all")}
            >
              {t("all")}
            </button>
            {languages.map((l) => (
              <button
                key={l}
                aria-pressed={filter === l}
                onClick={() => setFilter(l)}
              >
                {language(l, lang)}
              </button>
            ))}
          </div>
          <label className="sort-select">
            <span className="sr-only">Sort programmes</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="added">{t("addedDate")}</option>
              <option value="broadcast">{t("broadcast")}</option>
            </select>
          </label>
        </div>
        <EpisodeGrid episodes={episodes} />
      </section>
      <section className="station-strip">
        <SectionHead
          label={t("stations")}
          count={catalogue.stations.length}
          href="#/stations"
        />
        <div>
          {catalogue.stations.slice(0, 6).map((s) => (
            <a href={route("station", s.id)} key={s.id}>
              <span className="eyebrow">{country(s.country, lang)}</span>
              <strong>{s.name}</strong>
              <Icon name="arrow" size={18} />
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
